import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { r, s } from '@/theme';
import type { ToggleButtonProps } from '@mui/material/ToggleButton';
import type { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import type { MouseEvent, ReactElement } from 'react';

export type ToggleGroupProps<TValue extends string | number = string> = Omit<
  ToggleButtonGroupProps,
  'value' | 'onChange'
> & {
  value?: TValue | null;
  onChange?: (event: MouseEvent<HTMLElement>, value: TValue | null) => void;
};
export type ToggleItemProps = ToggleButtonProps;

export const ToggleGroup = <TValue extends string | number = string>({
  sx,
  ...rest
}: ToggleGroupProps<TValue>): ReactElement => {
  const mergedSx = [
    {
      borderRadius: r('md'),
      overflow: 'hidden',
      '& .MuiToggleButtonGroup-grouped': {
        borderColor: 'divider',
      },
      '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
        borderLeft: '1px solid',
        borderLeftColor: 'divider',
      },
      '& .MuiToggleButton-root': {
        backgroundColor: 'background.default',
        borderColor: 'divider',
        textTransform: 'none',
        paddingX: s('md'),
        paddingY: s('sm'),
      },
      '& .MuiToggleButton-root:hover, & .MuiToggleButton-root.Mui-focusVisible, & .MuiToggleButton-root:active':
        {
          backgroundColor: 'background.default',
        },
      '& .MuiToggleButton-root.Mui-selected': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      },
      '& .MuiToggleButton-root.Mui-selected .MuiTypography-root': { color: 'inherit' },
      '& .MuiToggleButton-root.Mui-selected:hover, & .MuiToggleButton-root.Mui-selected.Mui-focusVisible, & .MuiToggleButton-root.Mui-selected:active':
        { backgroundColor: 'primary.main' },
    },
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as ToggleButtonGroupProps['sx'];

  return <ToggleButtonGroup exclusive sx={mergedSx} {...rest} />;
};

export const ToggleItem = ({ sx, ...rest }: ToggleItemProps): ReactElement => {
  const mergedSx = [
    { borderRadius: r('md') },
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as ToggleButtonProps['sx'];
  return <ToggleButton sx={mergedSx} {...rest} />;
};
